import { extractTranslations } from '@assets/i18n/config';
import type { UserNotificationPreference } from '@interfaces/organizationUser';
import { UserContext } from '@providers/userContextProvider';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from 'hephaestus/web';
import { Close as CloseIcon } from 'hephaestus/webIcons';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationPreferencesSettings } from './NotificationPreferencesSettings';

export const hasNotificationSettings = (
  notificationPreferences: UserNotificationPreference[] | undefined,
) =>
  (notificationPreferences ?? []).some((preference) =>
    (preference.notificationGroup?.notificationChannels ?? []).some(
      (channel) => channel.value,
    ),
  );

export type NotificationPreferencesModalProp = {
  open: boolean;
  setOpen: (open: boolean) => void;
  notificationPreferences: UserNotificationPreference[] | undefined;
};

export const NotificationPreferencesModal = ({
  open,
  setOpen,
  notificationPreferences: sourceNotificationPreferences,
}: NotificationPreferencesModalProp) => {
  const { t } = useTranslation(['components', 'shared']);
  const { refetch } = useContext(UserContext);
  const [notificationPreferences, setNotificationPreferences] = useState<
    UserNotificationPreference[]
  >([]);

  useEffect(
    () =>
      setNotificationPreferences(
        (sourceNotificationPreferences ?? [])
          .filter((e) =>
            (e.notificationGroup?.notificationChannels ?? []).some(
              (el) => el.value,
            ),
          )
          .map((elem) => ({
            notificationGroup: {
              id: elem.notificationGroup.id,
              name: extractTranslations(elem.notificationGroup.name),
              description: elem.notificationGroup.description
                ? extractTranslations(elem.notificationGroup.description)
                : undefined,
              notificationChannels: (
                elem.notificationGroup?.notificationChannels ?? []
              ).map(({ type, value, allowEditByClient, setDefaultState }) => ({
                type,
                value,
                allowEditByClient,
                setDefaultState,
              })),
            },
            preferences: elem.preferences
              ? {
                  enableEmail: elem.preferences.enableEmail,
                  enableInApp: elem.preferences.enableInApp,
                  enablePush: elem.preferences.enablePush,
                }
              : undefined,
          })),
      ),
    [sourceNotificationPreferences],
  );

  const handleClose = () => {
    refetch();
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      sx={{
        '.MuiDialog-paper': {
          minHeight: 400,
        },
      }}
      fullWidth
    >
      <DialogTitle>
        {t('notificationSettings.title')}
        <IconButton aria-label={t('shared:close')} onClick={handleClose}>
          <CloseIcon fontSize='inherit' />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} useFlexGap>
          {notificationPreferences.map((elem) => (
            <NotificationPreferencesSettings
              key={elem.notificationGroup.id}
              notificationPreference={elem}
              notificationPreferences={notificationPreferences}
              setNotificationPreferences={setNotificationPreferences}
            />
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
